<template>
  <div class="spreadsheet-preview-wrapper position-relative">
    <!-- Loading Overlay (Semitransparent overlay with no spinner or text) -->
    <div
      v-if="loading"
      class="spreadsheet-loading-overlay"
    />

    <!-- Spreadsheet Mount Point -->
    <div
      ref="sheetContainer"
      class="spreadsheet-mount-point"
    />
  </div>
</template>

<script>
import moment from 'moment'
import Spreadsheet from 'x-data-spreadsheet/dist/xspreadsheet'
import 'x-data-spreadsheet/dist/xspreadsheet.css'

export default {
  name: 'SpreadsheetPreview',
  props: {
    sheetData: {
      type: [Object, Array],
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadingText: {
      type: String,
      default: 'Memuat transaksi...',
    },
  },
  data() {
    return {
      spreadsheet: null,
    }
  },
  watch: {
    sheetData: {
      handler(val) {
        if (this.spreadsheet && val) {
          const dataWithBorders = this.applyBordersToData(val)
          this.spreadsheet.loadData(dataWithBorders)
          if (this.spreadsheet.sheet) {
            this.spreadsheet.sheet.reload()
          }
          this.hookDraw()
        }
      },
      deep: false,
    },
  },
  mounted() {
    this.initSpreadsheet()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.spreadsheet) {
      this.spreadsheet = null
    }
  },
  methods: {
    applyBordersToData(sheetData) {
      if (!sheetData) return sheetData
      // Deep clone to avoid mutating reactive props and triggering infinite watcher loops
      const cloned = JSON.parse(JSON.stringify(sheetData))
      const target = Array.isArray(cloned) ? cloned[0] : cloned
      if (!target || !target.rows) return cloned

      // 1. Detect range dynamically across all rows and cells with value/style
      let minRow = Infinity
      let maxRow = -1
      let minCol = Infinity
      let maxCol = -1

      const { rows } = target
      Object.keys(rows).forEach(riKey => {
        const ri = parseInt(riKey, 10)
        if (Number.isNaN(ri)) return
        const row = rows[ri]
        if (row && row.cells) {
          Object.keys(row.cells).forEach(ciKey => {
            const ci = parseInt(ciKey, 10)
            if (Number.isNaN(ci)) return
            const cell = row.cells[ci]
            if (cell && (cell.text !== undefined || cell.style !== undefined)) {
              minRow = Math.min(minRow, ri)
              maxRow = Math.max(maxRow, ri)
              minCol = Math.min(minCol, ci)
              maxCol = Math.max(maxCol, ci)
            }
          })
        }
      })

      if (maxRow === -1 || maxCol === -1) return cloned

      // Ensure rows.len is dynamically set to cover all data rows plus breathing room
      target.rows.len = Math.max(maxRow + 20, 100)

      target.styles = target.styles || []
      const styleCache = new Map()

      const getStyleWithBorder = (baseStyleIndex, borderDef) => {
        const baseStyle = baseStyleIndex !== undefined && target.styles[baseStyleIndex]
          ? target.styles[baseStyleIndex]
          : {}
        const key = JSON.stringify({ base: baseStyle, border: borderDef })
        if (styleCache.has(key)) {
          return styleCache.get(key)
        }
        const newStyle = { ...baseStyle, border: borderDef }
        const newIndex = target.styles.length
        target.styles.push(newStyle)
        styleCache.set(key, newIndex)
        return newIndex
      }

      const borderColor = '#b8b5c3'
      const outerBorderColor = '#8c8998'

      // Parse merges if any to avoid drawing inner borders between merged cells
      const parsedMerges = []
      if (Array.isArray(target.merges)) {
        const parseCol = colStr => {
          let col = 0
          for (let i = 0; i < colStr.length; i += 1) {
            col = col * 26 + (colStr.charCodeAt(i) - 64)
          }
          return col - 1
        }
        target.merges.forEach(mergeStr => {
          if (typeof mergeStr !== 'string') return
          const parts = mergeStr.split(':')
          const m1 = parts[0].match(/^([A-Z]+)(\d+)$/)
          if (!m1) return
          const sci = parseCol(m1[1])
          const sri = parseInt(m1[2], 10) - 1
          let [eci, eri] = [sci, sri]
          if (parts.length > 1) {
            const m2 = parts[1].match(/^([A-Z]+)(\d+)$/)
            if (m2) {
              eci = parseCol(m2[1])
              eri = parseInt(m2[2], 10) - 1
            }
          }
          parsedMerges.push({
            sri, sci, eri, eci,
          })
        })
      }

      // Ensure cell.merge = [rn, cn] is set on each merge's top-left cell
      parsedMerges.forEach(m => {
        const rn = m.eri - m.sri
        const cn = m.eci - m.sci
        if (!rows[m.sri]) {
          rows[m.sri] = { cells: {} }
        }
        if (!rows[m.sri].cells) {
          rows[m.sri].cells = {}
        }
        if (!rows[m.sri].cells[m.sci]) {
          rows[m.sri].cells[m.sci] = { text: '' }
        }
        rows[m.sri].cells[m.sci].merge = [rn, cn]
      })

      const isInsideMergeAbove = (r, c) => parsedMerges.some(m => r > m.sri && r <= m.eri && c >= m.sci && c <= m.eci)
      const isInsideMergeLeft = (r, c) => parsedMerges.some(m => r >= m.sri && r <= m.eri && c > m.sci && c <= m.eci)

      for (let ri = minRow; ri <= maxRow; ri += 1) {
        if (!rows[ri]) {
          rows[ri] = { cells: {} }
        }
        if (!rows[ri].cells) {
          rows[ri].cells = {}
        }
        for (let ci = minCol; ci <= maxCol; ci += 1) {
          // If this cell is inside a merge but not the top-left cell, remove it so it is not rendered separately
          if (isInsideMergeAbove(ri, ci) || isInsideMergeLeft(ri, ci)) {
            if (rows[ri].cells[ci]) {
              delete rows[ri].cells[ci]
            }
          } else {
            if (!rows[ri].cells[ci]) {
              rows[ri].cells[ci] = { text: '' }
            }
            const cell = rows[ri].cells[ci]
            const isMergeTop = parsedMerges.find(m => m.sri === ri && m.sci === ci)
            const effectiveMaxRow = isMergeTop ? isMergeTop.eri : ri
            const effectiveMaxCol = isMergeTop ? isMergeTop.eci : ci

            const borderDef = {
              top: [ri === minRow ? 'medium' : 'thin', ri === minRow ? outerBorderColor : borderColor],
              bottom: [effectiveMaxRow === maxRow ? 'medium' : 'thin', effectiveMaxRow === maxRow ? outerBorderColor : borderColor],
              left: [ci === minCol ? 'medium' : 'thin', ci === minCol ? outerBorderColor : borderColor],
              right: [effectiveMaxCol === maxCol ? 'medium' : 'thin', effectiveMaxCol === maxCol ? outerBorderColor : borderColor],
            }
            cell.style = getStyleWithBorder(cell.style, borderDef)
          }
        }
      }

      return cloned
    },
    hookDraw() {
      if (
        this.spreadsheet
        && this.spreadsheet.sheet
        && this.spreadsheet.sheet.table
        && this.spreadsheet.sheet.table.draw
      ) {
        const { draw } = this.spreadsheet.sheet.table
        if (!draw._hooked) {
          const origDrawText = draw.text.bind(draw)
          draw.text = (mtxt, box, attr = {}, textWrap = true) => {
            let text = mtxt
            if (
              attr
              && attr.align === 'right'
              && (typeof mtxt === 'number' || (/^-?\d+(\.\d+)?$/.test(String(mtxt).trim()) && String(mtxt).trim() !== ''))
            ) {
              const num = Math.round(Number(mtxt))
              text = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            } else if (
              typeof mtxt === 'string'
              && /^\d{4}[-/]\d{2}[-/]\d{2}/.test(mtxt)
            ) {
              text = moment(mtxt).format('D/M/YYYY')
            }
            return origDrawText(text, box, attr, textWrap)
          }

          const dpr = () => window.devicePixelRatio || 1
          const npx = px => parseInt(px * dpr(), 10)
          const npxLine = px => {
            const n = npx(px)
            return n > 0 ? n - 0.5 : 0.5
          }

          // Hook draw.rect to eliminate white gap/margins around colored cells (e.g. Batal red rows)
          draw.rect = (box, dtextcb) => {
            const { ctx } = draw
            const {
              x, y, width, height, bgcolor,
            } = box
            ctx.save()
            ctx.beginPath()
            ctx.fillStyle = bgcolor || '#fff'

            const hasCustomBg = bgcolor && bgcolor !== '#ffffff' && bgcolor !== '#fff'
            if (hasCustomBg) {
              ctx.rect(npxLine(x), npxLine(y), npx(width), npx(height))
            } else {
              ctx.rect(npxLine(x + 1), npxLine(y + 1), npx(width - 2), npx(height - 2))
            }

            ctx.clip()
            ctx.fill()
            dtextcb()

            // Draw cell borders after fill so borders are crisp and never hidden by background
            if (box.borderTop || box.borderRight || box.borderBottom || box.borderLeft) {
              draw.strokeBorders(box)
            } else if (hasCustomBg) {
              // Stroke subtle right and bottom borders so cell separation remains clean
              ctx.beginPath()
              ctx.strokeStyle = '#e6e6e6'
              ctx.lineWidth = dpr() - 0.5
              ctx.moveTo(npxLine(x + width), npxLine(y))
              ctx.lineTo(npxLine(x + width), npxLine(y + height))
              ctx.moveTo(npxLine(x), npxLine(y + height))
              ctx.lineTo(npxLine(x + width), npxLine(y + height))
              ctx.stroke()
            }

            ctx.restore()
          }

          draw._hooked = true
        }
      }
    },
    initSpreadsheet() {
      const container = this.$refs.sheetContainer
      if (!container) return

      const options = {
        mode: 'read', // Read-only mode
        showToolbar: false,
        showGrid: true,
        showContextmenu: false,
        showBottomBar: false,
        view: {
          height: () => this.calculateHeight(),
          width: () => (container.clientWidth > 0 ? container.clientWidth : 1000),
        },
        row: {
          len: 100,
          height: 28,
        },
        col: {
          len: 16,
          width: 150,
          indexWidth: 50,
          minWidth: 60,
        },
      }

      const SpreadsheetConstructor = Spreadsheet && Spreadsheet.default ? Spreadsheet.default : Spreadsheet
      if (window.x_spreadsheet) {
        this.spreadsheet = window.x_spreadsheet(container, options)
      } else if (typeof SpreadsheetConstructor === 'function') {
        this.spreadsheet = new SpreadsheetConstructor(container, options)
      }

      this.hookDraw()

      if (this.sheetData && (Object.keys(this.sheetData).length > 0 || Array.isArray(this.sheetData))) {
        const dataWithBorders = this.applyBordersToData(this.sheetData)
        this.spreadsheet.loadData(dataWithBorders)
        if (this.spreadsheet.sheet) {
          this.spreadsheet.sheet.reload()
        }
        this.hookDraw()
      } else {
        // Load default empty sheet structure
        this.loadEmptyState()
        this.hookDraw()
      }
    },
    calculateHeight() {
      if (this.$el && this.$el.clientHeight > 0) {
        return this.$el.clientHeight
      }
      const container = this.$refs.sheetContainer
      if (container && container.clientHeight > 0) {
        return container.clientHeight
      }
      return Math.max(window.innerHeight - 60, 450)
    },
    calculateWidth() {
      if (this.$el && this.$el.clientWidth > 0) {
        return this.$el.clientWidth
      }
      const container = this.$refs.sheetContainer
      if (container && container.clientWidth > 0) {
        return container.clientWidth
      }
      return window.innerWidth || 1000
    },
    loadEmptyState() {
      if (!this.spreadsheet) return
      this.spreadsheet.loadData({
        name: 'Transactions',
        freeze: 'B2',
        styles: [
          {
            bgcolor: '#f8f9fa',
            font: { bold: true },
            align: 'center',
            valign: 'middle',
          },
        ],
        cols: {
          0: { width: 60 },
          1: { width: 180 },
          2: { width: 180 },
          3: { width: 110 },
          4: { width: 200 },
          5: { width: 110 },
          6: { width: 140 },
          7: { width: 160 },
          8: { width: 120 },
          9: { width: 120 },
          10: { width: 140 },
          11: { width: 110 },
        },
        rows: {
          0: {
            height: 56,
            cells: {
              0: { text: 'No.', style: 0 },
              1: { text: 'Customer Name', style: 0 },
              2: { text: 'Merchant', style: 0 },
              3: { text: 'Invoice Date', style: 0 },
              4: { text: 'Invoice Number', style: 0 },
              5: { text: 'Period', style: 0 },
              6: { text: 'Transaction Status', style: 0 },
              7: { text: 'Total Sales Amount', style: 0 },
              8: { text: 'Delivery Fee', style: 0 },
              9: { text: 'Insurance Fee', style: 0 },
              10: { text: 'Billed Total', style: 0 },
              11: { text: 'Source', style: 0 },
            },
          },
        },
      })
    },
    handleResize() {
      if (this.spreadsheet && this.spreadsheet.sheet) {
        this.spreadsheet.sheet.reload()
      }
    },
  },
}
</script>

<style lang="scss">
// x-data-spreadsheet was built assuming the standard browser content-box sizing model.
// In Bootstrap / Vuexy apps, *, *::before, *::after are reset to border-box.
// This causes the cell selection highlight (.x-spreadsheet-selector-area) and handle (.x-spreadsheet-selector-corner)
// to shrink inward by the border width (4px), leaving visible gaps along the grid lines.
// Restoring content-box ensures selection borders cover full cell bounds cleanly.
.x-spreadsheet,
.x-spreadsheet *,
.x-spreadsheet *::before,
.x-spreadsheet *::after {
  box-sizing: content-box !important;
}

// Ensure custom scrollbars are visible and styled with subtle, neutral tones
.x-spreadsheet-scrollbar {
  z-index: 100 !important;
  background-color: transparent !important;
  opacity: 0.35 !important;
  transition: opacity 0.2s ease !important;

  &:hover,
  &:focus,
  &:active {
    opacity: 1 !important;
  }

  &.vertical {
    width: 10px !important;

    &::-webkit-scrollbar {
      width: 10px !important;
      display: block !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #c8c6d1 !important;
      border-radius: 6px !important;
      border: 2px solid transparent !important;
      background-clip: padding-box !important;

      &:hover,
      &:focus,
      &:active {
        background: #9b99a6 !important;
        border: 2px solid transparent !important;
        background-clip: padding-box !important;
      }
    }

    &::-webkit-scrollbar-track {
      background: transparent !important;
    }
  }

  &.horizontal {
    height: 10px !important;

    &::-webkit-scrollbar {
      height: 10px !important;
      display: block !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #c8c6d1 !important;
      border-radius: 6px !important;
      border: 2px solid transparent !important;
      background-clip: padding-box !important;

      &:hover,
      &:focus,
      &:active {
        background: #9b99a6 !important;
        border: 2px solid transparent !important;
        background-clip: padding-box !important;
      }
    }

    &::-webkit-scrollbar-track {
      background: transparent !important;
    }
  }
}
</style>

<style scoped>
.spreadsheet-preview-wrapper {
  width: 100%;
  height: 100%;
  background-color: #fff;
  overflow: hidden;
  position: relative;
}

.spreadsheet-mount-point {
  width: 100%;
  height: 100%;
}

.spreadsheet-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 500;
  pointer-events: auto;
  cursor: wait;
}
</style>
